/**
 * ⟦C14 · A-133⟧ **La pose du héros, dans la chambre pixel art.**
 *
 * « Le sprite du héros posé sur le fond : pose selon l'état (au bureau pendant une
 * production, endormi pendant le sommeil, victoire aux célébrations, idle sinon). » Les
 * quatre poses viennent de la planche `assets/stills-8bit/r3-hero-sprites-8bit.png`, découpées
 * par `client/scripts/decoupe-sprites.py` en PNG transparents dans `client/public/sprites/`.
 *
 * Aucune des trois conditions n'est une nouvelle mécanique côté moteur (CLAUDE.md : « le
 * moteur est pur ») — les trois signaux existent déjà, projetés en une seule pose visuelle :
 * - `celebration` : un palier attend d'être fêté (`useRunStore.paliersEnAttente`, déjà la
 *   source de `PopupsPalier` — le même instant, un autre habillage) ;
 * - `sommeil` : un drapeau **local à `Studio.tsx`**, posé pendant l'appel `act({type:"sleep"})`
 *   et rien d'autre — `pending` seul ne suffit pas, il vaut `true` pour N'IMPORTE QUELLE
 *   action en vol (veille, commentaires…), et les montrer endormi serait un mensonge ;
 * - `travail` : le panneau Nouvelle vidéo/Atelier est ouvert (`emplacement.pathname`), qui est
 *   déjà comment `Studio.tsx` sait qu'un panneau est ouvert par-dessus la chambre (A-7
 *   décision 4).
 *
 * Priorité : une célébration l'emporte sur tout (c'est le plus gros moment de l'écran), puis
 * le sommeil, puis le travail — l'ordre dans lequel `Studio.tsx` les calcule ci-dessous.
 *
 * ⟦C15 · A-135 · A-136⟧ **Le genre et l'état de santé, ajoutés sans casser l'existant.** Les 14
 * planches `assets/img-src/avatar/av-{h,f}-{debout,assis}-{frais,fatigue,burnout}.png` (+
 * `av-{h,f}-couche.png`, un seul variant — on dort pareil quel que soit l'état) remplacent les
 * quatre sprites R3 pour `idle`/`travail`/`sommeil` — `debout`/`assis`/`couche` sont les trois
 * postures que ces planches dessinent. `victoire` n'a PAS de planche dédiée (le pack de 160 n'en
 * prévoit pas) : elle garde le sprite R3 tel quel, et c'est un choix, pas un oubli. `genre` est
 * `state.genre` (⟦A-4 §2⟧, `null` = état neutre, parties d'avant l'avenant) : `null` retombe
 * aussi sur R3, faute de savoir quel avatar dessiner. `zoneMental` réutilise `ZoneSM`
 * (`game/format.ts`, mêmes seuils que le reste de l'écran) réduit à 3 états — la planche n'a que
 * `frais`/`fatigue`/`burnout` : `rouge` et `crise` retombent tous les deux sur `burnout`.
 * Les deux paramètres sont **optionnels** (état neutre CLAUDE.md) : tout appel existant de
 * `spriteHeros(pose)` continue de rendre EXACTEMENT le même chemin R3 qu'avant ce chantier.
 */

import type { GenreAvatar } from "@maxime/shared";

import type { ZoneSM } from "./format";

export type PoseHeros = "idle" | "travail" | "sommeil" | "victoire";

export const POSES_HEROS: readonly PoseHeros[] = ["idle", "travail", "sommeil", "victoire"];

/** La posture que dessine la planche avatar pour chaque pose — `victoire` n'en a aucune. */
const POSTURE_PAR_POSE: Partial<Record<PoseHeros, "debout" | "assis" | "couche">> = {
  idle: "debout",
  travail: "assis",
  sommeil: "couche",
};

/** Les trois états de la planche avatar — `ZoneSM` en a quatre, `rouge`/`crise` fusionnent. */
function etatAvatar(zone: ZoneSM): "frais" | "fatigue" | "burnout" {
  if (zone === "vert") return "frais";
  if (zone === "fatigue") return "fatigue";
  return "burnout"; // rouge, crise
}

/**
 * Le chemin du sprite. `genre`/`zoneMental` absents (ou `pose === "victoire"`, ou `genre ===
 * null`) ⇒ repli sur les quatre sprites R3 (`client/public/sprites/heros-{pose}.png`), le
 * comportement d'avant ce chantier au caractère près. Sinon, la planche avatar (⟦C15⟧,
 * `client/public/avatar/av-{genre}-{posture}[-{etat}].png`).
 */
export function spriteHeros(pose: PoseHeros, genre?: GenreAvatar | null, zoneMental?: ZoneSM): string {
  const posture = POSTURE_PAR_POSE[pose];
  if (!posture || !genre) {
    return `/sprites/heros-${pose}.png`;
  }
  if (posture === "couche") {
    return `/avatar/av-${genre}-couche.png`;
  }
  return `/avatar/av-${genre}-${posture}-${etatAvatar(zoneMental ?? "vert")}.png`;
}

export interface SignauxPoseHeros {
  /** Un palier vient d'être franchi et attend sa pop-up (`paliersEnAttente.length > 0`). */
  celebration: boolean;
  /** L'action « sleep » est en vol (drapeau local à `Studio.tsx`, pas `pending` générique). */
  sommeil: boolean;
  /** Le panneau Nouvelle vidéo (l'Atelier) est ouvert par-dessus la chambre. */
  travail: boolean;
}

/** Résout les trois signaux en UNE pose — état neutre : aucun signal ⇒ `"idle"`. */
export function poseHeros({ celebration, sommeil, travail }: SignauxPoseHeros): PoseHeros {
  if (celebration) return "victoire";
  if (sommeil) return "sommeil";
  if (travail) return "travail";
  return "idle";
}

/**
 * ⟦C16 §1⟧ **Le sprite du héros AU SURVOL d'un objet de la chambre.**
 *
 * Verbatim de Mathieu : *« j'aimerais bien que l'avatar, en fonction de là où tu vas, il se
 * déplace […] l'avatar face au bureau si tu as la souris qui va sur le bureau, l'avatar qui
 * fait dodo sur le lit quand tu vas sur le lit, ou l'avatar qui regarde à la fenêtre quand tu
 * vas sur la fenêtre »* — et pourquoi : *« les boutons sont trop décorrélés du fond »*.
 *
 * ⚠️ ⟦C23 §3⟧ **Retour de Matt (la troisième fois) : « le personnage qui dort ne matche pas
 * dans le lit, le personnage qui va au bureau ne matche pas. »** Les sprites `assis`/`couché`
 * sont dessinés à un angle qui n'est celui d'aucune des planches de fond, et aucun calage ne le
 * rattrape. Décision de méthode (Système D) : au survol du lit/du bureau, l'avatar continue de
 * SE DÉPLACER vers l'objet (`Chambre.tsx`, `estObjetAvecSuivi`) mais **ne change plus de
 * posture** : il reste `idle` (debout), et c'est la PLAQUE de l'objet qui s'illumine à la place
 * (`app.css`, `.objet-bureau`/`.objet-lit` au survol/focus). Seule la fenêtre garde une pose de
 * survol dédiée : sa planche `av-{genre}-fenetre.png` regarde par la fenêtre sans s'y coucher
 * ni s'y asseoir, aucun angle à rattraper.
 *
 * Volontairement à PART de `spriteHeros`/`PoseHeros`/`POSES_HEROS` plutôt que d'ajouter une
 * cinquième pose : `POSES_HEROS` est verrouillé par `heros.test.ts`
 * (« couvre exactement idle/travail/sommeil/victoire ») et par la boucle de couverture de
 * `assets.test.ts`, qui multiplie CHAQUE pose par les 3 zones de santé mentale : la fenêtre n'a
 * qu'UNE planche par genre, sans déclinaison d'état. Un survol reste donc un calque au-dessus de
 * la pose d'écran, jamais un cinquième état qui la remplacerait ailleurs.
 *
 * `genre` absent (parties d'avant ⟦A-4 §2⟧) ⇒ `null` : pas de repli sur un sprite R3 ici — un
 * hover est un bonus visuel, jamais un `<img>` cassé si l'identité de l'avatar est inconnue.
 * `Chambre.tsx` retombe alors sur la pose d'écran normale, exactement comme si rien n'était
 * survolé.
 */
export type ObjetAvecPoseSurvol = "bureau" | "lit" | "fenetre";

/**
 * ⟦C23 §3⟧ Les objets qui font SUIVRE l'avatar au survol, les trois mêmes qu'avant ce chantier
 * (bureau/lit/fenêtre) : bureau et lit ne changent plus la POSTURE (voir `spriteHeroSurvol`),
 * mais l'avatar continue de s'y DÉPLACER, le mouvement et la posture étant deux choses
 * distinctes désormais. `Chambre.tsx` s'en sert pour décider QUEL objet fait bouger l'avatar,
 * indépendamment de ce que rend `spriteHeroSurvol`.
 */
export const OBJETS_AVEC_SUIVI: readonly ObjetAvecPoseSurvol[] = ["bureau", "lit", "fenetre"];

export function estObjetAvecSuivi(id: string): id is ObjetAvecPoseSurvol {
  return (OBJETS_AVEC_SUIVI as readonly string[]).includes(id);
}

/**
 * ⟦C23 §3⟧ Plus de troisième paramètre `zoneMental` : la seule posture de survol qui reste
 * (`fenetre`) n'en a jamais eu besoin (planche à variant unique, comme l'ex-`couché`) : bureau
 * et lit, qui en avaient besoin, ne renvoient plus de posture du tout. Le garder aurait laissé
 * un paramètre mort (`noUnusedParameters`, `tsconfig.json`). Les appelants (`Chambre.tsx`,
 * `heros.test.ts`) sont mis à jour dans le même commit.
 */
export function spriteHeroSurvol(objetId: string, genre: GenreAvatar | null | undefined): string | null {
  if (objetId !== "fenetre" || !genre) return null;
  return `/avatar/av-${genre}-fenetre.png`;
}
